/* Copyright (c) 2020-present CloudIO Inc */

import { IoAudit, IoDatasource } from '@cloudio-saas/datasource-types';
import {
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXResetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Divider,
  IconButton,
  InputBase,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import useDebounce from 'hooks/useDebounce';
import Icon from 'icons/Icon';
import { usePending } from 'platform/PlatformHooks';
import { PlatformContext } from 'platform/PlatformProvider';
import React, {
  startTransition,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import Store from 'store/Store';
import { DBRow, isFromDB } from 'store/storeTypes';
import {
  useStoreCurrentRecord,
  useStoreBusy,
  useStoreDirty,
} from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import {
  recordId,
  useIoDatasourceAttributesStore,
  useIoDatasourceEditStore,
} from 'store/utils/utils';
import { maximizeSidebarWidthAtom } from 'utils/state';
import WaveDots from 'utils/WaveDots';
import { validateCode } from 'wf/utils/WFUtils';
import { openURL } from '../../../../actions/ActionOpenURL/ActionOpenURL';
import {
  DSView,
  sidebarActiveDatasourceIdAtom,
  sidebarDatasourceEditModeAtom,
  sidebarDataSourceFilterAtom,
  sidebarDataSourceFilterResultsSizeAtom,
  sidebarDataSourceWizardTitleAtom,
} from '../../atoms';
import ExpandCollapseIcon from '../ExpandCollapseIcon';
import { scripts } from './constants';
import { sidebarDatasourceUploadFileAtom } from './Wizard/DataSourceWizardTypes';
import { isViewOnlyDeveloper } from 'root/useUser';
import { ObjectRow, useFetch } from '../SQLWorksheet/state';
import { getErrorMessage } from 'utils/isEmpty';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
    minWidth: 80,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

export default function DataSourceHeader({
  setView,
  view,
  store: listStore,
  appUid,
}: {
  appUid: string;
  setView: (_view: DSView) => void;
  view: DSView;
  store: Store<IoDatasource>;
}): React.ReactElement {
  const platform = useContext(PlatformContext);
  const editStore = useIoDatasourceEditStore(appUid);
  const dirty = useStoreDirty(editStore);
  const busy = useStoreBusy(editStore);
  const { record } = useStoreCurrentRecord(editStore);
  const setEditId = useStateXAtomSetter(sidebarActiveDatasourceIdAtom);
  const editMode = useStateXAtomValue(sidebarDatasourceEditModeAtom);
  const attrStore = useIoDatasourceAttributesStore(appUid);
  const auditStore = useDataStore<IoAudit>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoAudit',
    alias: 'DSIoAudit-alias',
  });
  // const dsAccessStore = useDataStore<IoDatasourceAccess>(
  //   '$$Sidebar$$',
  //   'IoDatasourceAccessV',
  //   'IoDatasourceAccessV-alias',
  // );
  const classes = useStyles();
  const [search, setSearch] = useStateXAtom(sidebarDataSourceFilterAtom);
  const title = useStateXAtomValue(sidebarDataSourceWizardTitleAtom);
  const [text, setText] = useState(search);
  const ref = useRef<HTMLInputElement>(null);
  const filterSize = useStateXAtomValue(sidebarDataSourceFilterResultsSizeAtom);
  const [isPending, setPending] = usePending();
  const reset = useStateXResetter();
  const set = useStateXSetter();
  const [debouce] = useDebounce((_text: string) => {
    startTransition(() => {
      platform.forceUpdater(() => {
        setSearch(_text);
      });
    });
  });
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const callFetch = useFetch();

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setText(e.target.value);
    debouce(300, e.target.value);
  }

  const onClick = useCallback(() => {
    if (view === 'edit') {
      listStore.reset();
      editStore.reset();
      if (editMode === 'Edit') {
        setView('list');
      } else {
        setView('wizard');
      }
    } else if (view === 'wizard') {
      if (editMode === 'File') {
        reset(sidebarDatasourceUploadFileAtom);
        reset(sidebarDatasourceEditModeAtom);
        reset(sidebarDataSourceWizardTitleAtom);
      } else {
        setView('list');
      }
    } else if (view === 'next') {
      setView('list');
    } else {
      ref.current?.focus();
    }
  }, [editMode, editStore, listStore, reset, setView, view]);

  const isTableExists = async (
    value: string,
    connectionCode: string | undefined,
  ) => {
    try {
      const json = await callFetch(
        connectionCode ? connectionCode : appUid,
        '$TABLE_CHECK$@' + value,
      );
      if (json.status === 'ERROR') {
        platform.showError(json.message || 'Error');
      } else {
        return (json.data as unknown as ObjectRow[]).find(
          (tb) => tb['TABLE_NAME'] === value,
        );
      }
    } catch (e) {
      platform.showError(getErrorMessage(e));
    }
    return false;
  };

  return (
    <div className={classes.root}>
      <IconButton
        data-testid="back"
        onClick={onClick}
        className={classes.iconButton}
        aria-label="back"
        key="back"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {view === 'list' ? 'Filter' : 'Back'}
        </span>
        <Icon iconName={view === 'list' ? 'search' : 'chevron-left'} />
      </IconButton>
      {view === 'wizard' || view === 'next' ? (
        <Typography className={classes.input}>{title}</Typography>
      ) : view === 'edit' ? (
        <Typography className={classes.input}>
          {isFromDB(record)
            ? 'Edit'
            : record?.copy_from
            ? 'Duplicate'
            : 'Add New'}{' '}
          DataSource
        </Typography>
      ) : (
        <InputBase
          data-testid="filter"
          inputRef={ref}
          className={classes.input}
          placeholder="Search DataSource"
          inputProps={{
            'aria-label': 'search datasource',
            'data-testid': 'search-datasource',
          }}
          value={text ?? ''}
          onChange={onChange}
          autoFocus
        />
      )}
      {view === 'list' ? (
        <Typography
          color="textSecondary"
          variant="body2"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          ml={1}
        >
          {filterSize} data sources
        </Typography>
      ) : null}
      <Divider className={classes.divider} orientation="vertical" />
      {view !== 'next' && view !== 'wizard' && (view !== 'edit' || dirty) ? (
        <IconButton
          LinkComponent="a"
          data-tip={view === 'edit' ? 'Save' : 'Create New DataSource'}
          data-testid="action"
          disabled={busy || isViewOnlyUser}
          color="primary"
          title={view === 'edit' ? 'Save' : 'Create New DataSource'}
          aria-label={view === 'edit' ? 'Save' : 'Create New DataSource'}
          onClick={async () => {
            if (view === 'edit') {
              const row = editStore.getCurrentRecord();
              if (row) {
                for (let i = 0; i < scripts.length; i++) {
                  const script = scripts[i];
                  if (
                    script.attribute !== 'Module' &&
                    script.attribute !== 'ScheduledJob' &&
                    script.attribute !== 'WebAPI' &&
                    script.attribute !== 'authApi' &&
                    script.attribute !== 'describeApi' &&
                    script.attribute !== 'pageController' &&
                    script.attribute !== 'postApi' &&
                    script.attribute !== 'queryApi' &&
                    script.attribute !== 'wfNode' &&
                    script.attribute !== 'managerJs' &&
                    script.attribute !== 'componentJs' &&
                    script.attribute !== 'TestScripts' &&
                    script.attribute !== 'wf' &&
                    script.attribute !== 'customTemplates'
                  ) {
                    const msg = validateCode(row[script.attribute]);
                    if (msg) {
                      platform.showError(
                        `Error in ${script.title} Script: ${msg}`,
                      );
                      return;
                    }
                  }
                }
              }
              const fromDB = isFromDB(row);
              if (editMode === 'SQL' && row) {
                editStore.updateRecord(recordId(row), {
                  typeValue: 'dummy',
                  preQueryScript: `let sb = \`SELECT  * FROM (${row.sql?.replace(
                    /`/g,
                    '\\`',
                  )}) X\`;\n\nquery.fullSQL=sb;`,
                });
              }
              if (
                row &&
                row.datasourceType === 'Table' &&
                fromDB &&
                row._orig?.typeValue &&
                row._orig?.typeValue !== row.typeValue &&
                row.typeValue &&
                !(await isTableExists(row.typeValue, row.objectConnectionCode))
              ) {
                platform.showError(
                  `Table with name ${row.typeValue} doesn't exists!`,
                );
                return;
              }
              editStore.save({ feedback: 'NONE' }).then(async (r) => {
                if (r) {
                  let feedback = '';
                  if (fromDB) {
                    // stay on the same screen after save
                    // setView('list');
                    feedback = `DataSource ${row?.datasourceUid} updated successfully!`;
                    auditStore.clear();
                  } else if (row) {
                    const dbrow =
                      editStore.getCurrentRecord() as DBRow<IoDatasource>;
                    const listRow = listStore.getCurrentRecord();

                    if (listRow && dbrow && listRow._cid) {
                      listStore.replaceClientId(listRow._cid, dbrow._id, {
                        ...dbrow,
                      });
                    }

                    await attrStore.clear();
                    // set the active ds id to re-query the attributes in the attr list UI
                    setEditId(dbrow.datasourceUid);
                    setView('next');
                    set(
                      sidebarDataSourceWizardTitleAtom,
                      'Choose the next step',
                    );

                    if (row.copy_from) {
                      feedback = `DataSource ${dbrow.datasourceUid} copied successfully!`;
                    } else {
                      feedback = `DataSource ${dbrow.datasourceUid} created successfully!`;
                    }
                  }
                  platform.showSuccess(feedback);
                }
              });
            } else {
              // clear the current active id to avoid querying in the edit UI
              setEditId('');
              reset(sidebarDataSourceWizardTitleAtom);
              reset(sidebarDatasourceUploadFileAtom);
              reset(sidebarDatasourceEditModeAtom);
              set(maximizeSidebarWidthAtom, 'Y');
              setView('wizard');
            }
          }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            {view === 'edit' ? 'Save' : 'Create New DataSource'}
          </span>
          {busy ? (
            <WaveDots active style={{ marginLeft: 8 }} />
          ) : (
            <Icon
              color="primary"
              disabled={isViewOnlyUser}
              iconName={view === 'edit' ? 'check' : 'plus'}
            />
          )}
        </IconButton>
      ) : null}
      {view !== 'edit' && view !== 'next' && view !== 'wizard' ? (
        <IconButton
          data-testid="refresh-icon"
          data-tip="Refresh"
          disabled={isPending}
          color="primary"
          aria-label="Refresh"
          onClick={() => {
            platform.startAsyncTransition(async () => {
              await listStore.refresh();
            }, setPending);
          }}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Refresh
          </span>
          {isPending ? (
            <WaveDots active />
          ) : (
            <Icon color="primary" iconName="redo" />
          )}
        </IconButton>
      ) : null}
      <IconButton
        data-testid="help-icon"
        color="primary"
        aria-label="Help"
        onClick={() => {
          openURL('https://next-docs.cloudio.io/datasource/scripts', true);
        }}
        data-tip="Help"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Help
        </span>
        <Icon iconName="question" />
      </IconButton>
      <ExpandCollapseIcon />
    </div>
  );
}
